import { useEffect, useState } from "react";
import { FileSlot } from "../components/FileSlot";
import { useI18n } from "../i18n";
import { loadDocFiles, loadDocuments, removeFile, setDocument } from "../data";
import { DocBadge } from "../components/Badges";
import type { DocRow, Workspace } from "../types";

const ATTENTION = new Set(["overdue", "pending", "expiring", "expired", "rejected"]);

/** The hiring documents tracker: everything that still needs attention, across all employees. */
export function Documents({ ws, onOpen }: { ws: Workspace; onOpen: (employmentId: string) => void }) {
  const { t } = useI18n();
  const [rows, setRows] = useState<DocRow[] | null>(null);
  const [all, setAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      setRows(await loadDocuments(ws.membership.company_id));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    }
  }
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.membership.company_id]);

  const count = (s: string) => (rows ?? []).filter((r) => r.status === s && r.required).length;
  const shown = (rows ?? []).filter((r) => all || (ATTENTION.has(r.status) && (r.required || r.status !== "pending")));

  return (
    <div className="page">
      {error && <p className="alert danger" role="alert">{error}</p>}
      {rows === null ? (
        <p className="muted">{t("loading")}</p>
      ) : (
        <>
          <div className="stats">
            <div className="stat"><span className="stat-value danger-text">{count("overdue")}</span><span className="stat-label">{t("overdueCount")}</span></div>
            <div className="stat"><span className="stat-value">{count("pending")}</span><span className="stat-label">{t("pendingCount")}</span></div>
            <div className="stat"><span className="stat-value">{count("expiring") + count("expired")}</span><span className="stat-label">{t("expiringCount")}</span></div>
          </div>
          <section className="card">
            <div className="card-head">
              <h2>{all ? t("documents") : t("needsAttention")}</h2>
              <label className="check">
                <input id="docs-all" type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> {t("showAll")}
              </label>
            </div>
            {shown.length === 0 ? (
              <p className="muted">{t("allDone")}</p>
            ) : (
              <DocumentList ws={ws} rows={shown} onChanged={refresh} showEmployee onOpen={onOpen} />
            )}
          </section>
        </>
      )}
    </div>
  );
}

interface ListProps {
  ws: Workspace;
  rows: DocRow[];
  onChanged: () => void | Promise<void>;
  showEmployee: boolean;
  onOpen?: (employmentId: string) => void;
}

export function DocumentList({ ws, rows, onChanged, showEmployee, onOpen }: ListProps) {
  const { t, pick } = useI18n();
  const canManage = ws.permissions.has("documents.manage");
  const [editing, setEditing] = useState<string | null>(null);
  const [received, setReceived] = useState(new Date().toISOString().slice(0, 10));
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<Map<string, { id: string; path: string | null }>>(new Map());
  const cid = ws.membership.company_id;
  const loadFiles = () => loadDocFiles(cid).then((x) => setFiles(new Map(x.map((d) => [`${d.employment_id}:${d.document_type_id}`, { id: d.id, path: d.file_path }])))).catch(() => undefined);
  useEffect(() => { void loadFiles(); }, [cid, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(r: DocRow, status: "received" | "waived" | null) {
    setBusy(true);
    setError(null);
    try {
      const f = files.get(`${r.employment_id}:${r.document_type_id}`);
      if (status === null && f?.path) await removeFile("documents", f.id);
      await setDocument(ws.membership.company_id, r.employment_id, r.document_type_id, status, received, expiry);
      setEditing(null);
      setExpiry("");
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <p className="alert danger" role="alert">{error}</p>}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {showEmployee && <th>{t("fullName")}</th>}
              <th>{t("documents")}</th>
              <th>{t("dueDate")}</th>
              <th>{t("status")}</th>
              {canManage && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const key = `${r.employment_id}:${r.document_type_id}`;
              const done = r.status === "received" || r.status === "waived" || r.status === "expiring" || r.status === "expired";
              return (
                <tr key={key}>
                  {showEmployee && (
                    <td>
                      <button type="button" className="link" onClick={() => onOpen?.(r.employment_id)}>{r.full_name}</button>
                    </td>
                  )}
                  <td>
                    {pick(r.document_name_en, r.document_name_ar)}
                    {r.required && <span className="req" title={t("required")}> *</span>}
                    {r.expiry_date && <div className="muted small-note">{t("expiryDate")}: <span className="mono">{r.expiry_date}</span></div>}
                    {(() => { const f = files.get(key); return f && r.status !== "waived" && r.status !== "rejected" ? <div><FileSlot companyId={cid} area="documents" ownerId={r.employment_id} recordId={f.id} path={f.path} canEdit={canManage} onChanged={loadFiles} /></div> : null; })()}
                  </td>
                  <td className="mono">{r.due_date}</td>
                  <td><DocBadge status={r.status} /></td>
                  {canManage && (
                    <td className="cell-actions">
                      {editing === key ? (
                        <div className="inline-edit">
                          <label className="field compact"><span>{t("receivedOn")}</span>
                            <input id={`rcv-${key}`} type="date" value={received} onChange={(e) => setReceived(e.target.value)} /></label>
                          <label className="field compact"><span>{t("expiryDate")}</span>
                            <input id={`exp-${key}`} type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} /></label>
                          <div className="row">
                            <button type="button" className="btn secondary small" onClick={() => setEditing(null)} disabled={busy}>{t("cancel")}</button>
                            <button type="button" className="btn primary small" onClick={() => act(r, "received")} disabled={busy || !received}>{t("save")}</button>
                          </div>
                        </div>
                      ) : done ? (
                        <button type="button" className="btn secondary small" onClick={() => act(r, null)} disabled={busy}>{t("undo")}</button>
                      ) : (
                        <div className="row">
                          <button type="button" className="btn primary small" onClick={() => { setEditing(key); setExpiry(""); }} disabled={busy}>{t("markReceived")}</button>
                          <button type="button" className="btn secondary small" onClick={() => act(r, "waived")} disabled={busy}>{t("waive")}</button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
